import type { BudgetState } from "./types.js";

export interface BudgetCheckResult {
  ok: boolean;
  warning?: string;
  suggestion?: string;
}

/**
 * Check whether an estimated cost fits within the remaining budget.
 *
 * Projects a daily burn rate and warns when:
 * - The task itself would exceed the remaining budget
 * - The current burn rate will exhaust the budget before month end
 * - Spend is above 80% of the monthly limit
 */
export function checkBudget(
  budget: BudgetState,
  estimatedCost: number,
): BudgetCheckResult {
  // Guard: zero or negative monthly limit blocks all spending
  if (budget.monthlyLimit <= 0) {
    return {
      ok: false,
      warning: "Monthly budget limit is zero or negative — all spending blocked",
      suggestion: "Set a positive monthly budget limit",
    };
  }

  const remaining = budget.monthlyLimit - budget.currentSpend;

  // Guard: remaining budget calculation produced invalid result
  if (!isFinite(remaining)) {
    return {
      ok: false,
      warning: "Budget calculation produced invalid result",
      suggestion: "Check budget configuration values",
    };
  }

  const dailyBurnRate =
    budget.daysRemaining > 0
      ? remaining / budget.daysRemaining
      : remaining;

  // Case 1: Task itself exceeds remaining budget
  if (estimatedCost > remaining) {
    return {
      ok: false,
      warning: `Estimated cost $${estimatedCost.toFixed(4)} exceeds remaining budget $${remaining.toFixed(2)}`,
      suggestion:
        "Consider using a cheaper model or deferring this task to the next billing cycle",
    };
  }

  // Case 2: Already over 90% spent
  const spendRatio = budget.currentSpend / budget.monthlyLimit;
  if (spendRatio >= 0.9) {
    return {
      ok: true,
      warning: `Budget ${(spendRatio * 100).toFixed(0)}% spent ($${budget.currentSpend.toFixed(2)}/$${budget.monthlyLimit.toFixed(2)}) with ${budget.daysRemaining} days remaining`,
      suggestion:
        "Switch to economy-tier models for remaining tasks this month",
    };
  }

  // Case 3: Burn rate will exhaust budget before month end
  const projectedSpend = budget.currentSpend + estimatedCost;
  const projectedDailyRate =
    budget.daysRemaining > 1
      ? (projectedSpend / (30 - budget.daysRemaining + 1)) *
        30
      : projectedSpend;

  if (projectedDailyRate > budget.monthlyLimit && spendRatio >= 0.8) {
    return {
      ok: true,
      warning: `At current pace, projected to exceed monthly limit by $${(projectedDailyRate - budget.monthlyLimit).toFixed(2)}`,
      suggestion:
        "Consider batching requests or using cheaper models to stay within budget",
    };
  }

  // Case 4: Over 80% threshold — mild warning
  if (spendRatio >= 0.8) {
    return {
      ok: true,
      warning: `Budget ${(spendRatio * 100).toFixed(0)}% spent with ${budget.daysRemaining} days remaining`,
    };
  }

  // All clear
  return { ok: true };
}

/**
 * Calculate the daily budget remaining.
 */
export function dailyBudgetRemaining(budget: BudgetState): number {
  const remaining = budget.monthlyLimit - budget.currentSpend;
  return budget.daysRemaining > 0 ? remaining / budget.daysRemaining : remaining;
}
